import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';
import ModalDialog from './ModalDialog.jsx';

function DisclaimerModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  return (
    <ModalDialog onClose={onClose} labelledBy="disclaimer-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <h2 id="disclaimer-title" className="text-xl font-bold text-stone-800">Disclaimer & User Agreement</h2>
            <button onClick={onClose} className="text-stone-500 hover:text-stone-600 transition-colors" aria-label="Close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-sm text-stone-700" tabIndex={0} role="region" aria-label="Disclaimer content">
          <div>
            <h3 className="font-semibold text-stone-800 mb-2">Purpose</h3>
            <p>
              The ArguMentor is a guided conflict resolution tool designed to promote and encourage 
              healthy communication. It provides a structured framework for participants to work through 
              disagreements constructively.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-stone-800 mb-2">Not a Mental Health Service</h3>
            <p>
              This application does not provide mental health counseling, therapy, medical advice, or 
              professional services of any kind. The ArguMentor is not a substitute for professional 
              mental health care, legal counsel, or any other professional service.
            </p>
          </div>

          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2">⚠️ Safety Notice</h3>
            <p className="text-red-700">
              If you or someone you know is experiencing thoughts of self-harm, feels unsafe, or is in 
              immediate danger, please <strong>call 911</strong> immediately. You can also contact the 
              <strong> 988 Suicide & Crisis Lifeline</strong> by calling or texting <strong>988</strong>.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-stone-800 mb-2">User Conduct</h3>
            <p>
              By using The ArguMentor, you agree to communicate respectfully, refrain from threats or 
              harassment, and engage in good faith during the conflict resolution process. Abusive 
              language, intimidation, or bad-faith participation is not permitted.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-stone-800 mb-2">Privacy & Shared Data</h3>
            <p>
              Conflict data (including statements, paraphrases, and responses) is shared between all 
              assigned participants and mentors. Do not share sensitive personal information that you 
              would not want other participants to see.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-stone-800 mb-2">Limitation of Liability</h3>
            <p>
              The ArguMentor and its creators are not responsible for the outcomes of any conflict 
              resolution process facilitated through this tool. Use of this application is at your 
              own discretion and risk.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-stone-200">
          <button
            onClick={onClose}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

// Terms of Service Modal
function TermsOfServiceModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  return (
    <ModalDialog onClose={onClose} labelledBy="terms-of-service-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 id="terms-of-service-title" className="text-xl font-bold text-stone-800">Terms of Service</h2>
              <p className="text-xs text-stone-500 mt-1">Last Updated: {today}</p>
            </div>
            <button onClick={onClose} className="text-stone-500 hover:text-stone-600 transition-colors" aria-label="Close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-sm text-stone-700 leading-relaxed" tabIndex={0} role="region" aria-label="Terms of Service content">
          <p>
            Welcome to <strong>The ArguMentor</strong>, a product of <strong>The ArguMentor, LLC</strong> 
            ("Company," "we," "us," or "our"), with its principal place of business in Land O' Lakes, Florida.
          </p>
          <p>
            By accessing or using The ArguMentor application (the "Service"), you agree to be bound by these 
            Terms of Service ("Terms"). If you do not agree, do not use the Service.
          </p>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">1. ACCEPTANCE OF TERMS</h3>
            <p className="mb-2">
              By creating an account, accessing, or using the Service, you confirm that you are at least 13 years of age 
              and have the legal capacity to enter into these Terms. If you are under 18, you represent that your parent 
              or legal guardian has reviewed and agreed to these Terms on your behalf.
            </p>
            <p>
              We reserve the right to modify these Terms at any time. Continued use of the Service after changes 
              constitutes acceptance of the updated Terms. We will make reasonable efforts to notify users of 
              material changes.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">2. DESCRIPTION OF SERVICE</h3>
            <p className="mb-2">
              The ArguMentor is a guided conflict resolution tool that provides a structured framework for 
              participants to work through disagreements constructively. The Service includes features such as 
              structured communication steps, paraphrasing exercises, alternative exploration, mentor oversight, 
              mood tracking, and AI-assisted suggestions.
            </p>
            <p className="mb-2">
              <strong>The Service is NOT:</strong> a mental health service, counseling platform, therapy tool, 
              legal service, mediation service, or substitute for any professional service. The Service does not 
              provide medical, psychological, legal, or professional advice of any kind.
            </p>
            <p>
              The Service is currently in a beta/pre-release state and may contain bugs, errors, or incomplete features. 
              The Service is provided "AS IS" without warranties.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">3. USER ACCOUNTS</h3>
            <p className="mb-2">
              <strong>3.1 Registration.</strong> You must provide a valid email address and your name to create an account. 
              You agree to provide accurate, current, and complete information and to update such information as necessary.
            </p>
            <p className="mb-2">
              <strong>3.2 Account Security.</strong> You are responsible for maintaining the security of your account. 
              The Service uses email-based verification (one-time codes) for authentication. You agree to notify us 
              immediately of any unauthorized use of your account.
            </p>
            <p>
              <strong>3.3 Account Termination.</strong> We may suspend or terminate your account at any time, with or 
              without cause, with or without notice. You may request account deletion by contacting us.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">4. USER CONDUCT</h3>
            <p className="mb-2">You agree to use the Service in good faith and in compliance with all applicable laws. You shall NOT:</p>
            <p className="mb-1 pl-4">(a) Use the Service to threaten, harass, intimidate, stalk, or abuse any person;</p>
            <p className="mb-1 pl-4">(b) Submit content that is defamatory, obscene, or promotes violence;</p>
            <p className="mb-1 pl-4">(c) Impersonate any person or entity;</p>
            <p className="mb-1 pl-4">(d) Attempt to gain unauthorized access to the Service or its systems;</p>
            <p className="mb-1 pl-4">(e) Use the Service to transmit malware, viruses, or harmful code;</p>
            <p className="mb-1 pl-4">(f) Interfere with or disrupt the Service or servers;</p>
            <p className="mb-1 pl-4">(g) Use automated means (bots, scrapers) to access the Service;</p>
            <p className="mb-2 pl-4">(h) Use the Service for any illegal purpose.</p>
            <p>
              We reserve the right to remove content and suspend accounts that violate these standards at our sole discretion.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">5. CONTENT AND DATA</h3>
            <p className="mb-2">
              <strong>5.1 User Content.</strong> You retain ownership of the content you submit to the Service (statements, 
              paraphrases, alternatives, messages, etc.). By submitting content, you grant the Company a non-exclusive, 
              worldwide, royalty-free license to use, store, display, and process your content solely for the purpose of 
              providing and improving the Service.
            </p>
            <p className="mb-2">
              <strong>5.2 Shared Content.</strong> Conflict data, including statements, paraphrases, and messages, is 
              shared between all participants and mentors assigned to a conflict. By participating, you consent to this 
              sharing. Do not submit sensitive personal information (e.g., Social Security numbers, financial account details, 
              medical records) to the Service.
            </p>
            <p>
              <strong>5.3 Content Monitoring.</strong> The Service includes automated content monitoring for safety purposes, 
              including detection of language indicating potential self-harm or harm to others. If such content is detected, 
              crisis resources may be displayed and assigned mentors may be notified. See our Privacy Policy for details.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">6. AI-POWERED FEATURES</h3>
            <p className="mb-2">
              The Service may include AI-powered features such as paraphrase suggestions and content assistance. 
              AI-generated suggestions are provided as-is and may be inaccurate, incomplete, or inappropriate. 
              You are solely responsible for reviewing and editing any AI-generated content before submission.
            </p>
            <p>
              AI features are optional aids and do not constitute professional advice. The Company makes no warranties 
              regarding the quality, accuracy, or suitability of AI-generated content.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">7. MENTOR ROLE</h3>
            <p className="mb-2">
              Users may be assigned as "mentors" to oversee conflict resolution processes. Mentors are peer facilitators 
              and are NOT licensed counselors, therapists, mediators, or professionals. The Company does not vet, certify, 
              or endorse mentors in any capacity.
            </p>
            <p>
              Mentors may receive safety alerts if concerning content is detected. Mentors are not obligated to act on 
              such alerts and are not responsible for the safety or wellbeing of participants.
            </p>
          </div>

          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h3 className="font-bold text-red-800 mb-2">8. SAFETY NOTICE</h3>
            <p className="text-red-700 mb-2">
              If you or someone you know is in immediate danger or experiencing a medical or mental health emergency, 
              <strong> call 911</strong> immediately.
            </p>
            <p className="text-red-700">
              For emotional crisis support, contact the <strong>988 Suicide & Crisis Lifeline</strong> by calling or 
              texting <strong>988</strong> (available 24/7). The Service is not a crisis intervention tool and should 
              not be relied upon in emergencies.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">9. INTELLECTUAL PROPERTY</h3>
            <p className="mb-2">
              All intellectual property rights in the Service, including but not limited to software, design, logos, 
              trademarks, methodologies, frameworks, and documentation, are the exclusive property of The ArguMentor, LLC. No right, title, or interest is transferred to you except the limited right to use 
              the Service in accordance with these Terms.
            </p>
            <p>
              "The ArguMentor," the ArguMentor owl logo, and related marks are trademarks of the Company. You may not 
              use these marks without our prior written consent.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">10. DISCLAIMER OF WARRANTIES</h3>
            <p className="uppercase text-xs tracking-wide">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
              NON-INFRINGEMENT, OR THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. THE COMPANY DOES 
              NOT WARRANT THAT THE SERVICE WILL MEET YOUR REQUIREMENTS OR THAT ANY RESULTS OBTAINED FROM THE SERVICE 
              WILL BE ACCURATE OR RELIABLE. YOU USE THE SERVICE AT YOUR OWN RISK.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">11. LIMITATION OF LIABILITY</h3>
            <p className="uppercase text-xs tracking-wide">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY, ITS OFFICERS, 
              DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, USE, GOODWILL, OR PROFITS, ARISING OUT OF 
              OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, 
              TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, EVEN IF THE COMPANY HAS BEEN ADVISED 
              OF THE POSSIBILITY OF SUCH DAMAGES. THE COMPANY'S TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED ONE 
              HUNDRED DOLLARS ($100.00 USD).
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">12. INDEMNIFICATION</h3>
            <p>
              You agree to indemnify, defend, and hold harmless the Company and its officers, directors, employees, 
              and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses 
              (including reasonable attorneys' fees) arising out of or related to your use of the Service, your 
              violation of these Terms, or your violation of any rights of another.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">13. GOVERNING LAW & DISPUTE RESOLUTION</h3>
            <p className="mb-2">
              These Terms shall be governed by and construed in accordance with the laws of the State of Florida, 
              without regard to its conflict of law provisions.
            </p>
            <p>
              Any dispute arising out of or relating to these Terms or the Service shall be resolved through binding 
              arbitration administered by the American Arbitration Association in Pasco County, Florida, except that 
              either party may seek injunctive relief in a court of competent jurisdiction. You waive any right to 
              participate in a class action lawsuit or class-wide arbitration.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">14. GENERAL PROVISIONS</h3>
            <p className="mb-2">
              <strong>Severability.</strong> If any provision is held unenforceable, the remaining provisions continue 
              in full force.
            </p>
            <p className="mb-2">
              <strong>Waiver.</strong> Failure to enforce any provision does not constitute a waiver of that provision.
            </p>
            <p className="mb-2">
              <strong>Entire Agreement.</strong> These Terms, together with our Privacy Policy and any other agreements 
              referenced herein, constitute the entire agreement between you and the Company.
            </p>
            <p>
              <strong>Contact.</strong> Questions about these Terms may be directed to: <strong>info@4hci.com</strong>
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-stone-200">
          <button
            onClick={onClose}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

// Privacy Policy Modal
function PrivacyPolicyModal({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  return (
    <ModalDialog onClose={onClose} labelledBy="privacy-policy-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 id="privacy-policy-title" className="text-xl font-bold text-stone-800">Privacy Policy</h2>
              <p className="text-xs text-stone-500 mt-1">Last Updated: {today}</p>
            </div>
            <button onClick={onClose} className="text-stone-500 hover:text-stone-600 transition-colors" aria-label="Close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-sm text-stone-700 leading-relaxed" tabIndex={0} role="region" aria-label="Privacy Policy content">
          <p>
            <strong>The ArguMentor, LLC</strong> ("Company," "we," "us," or "our") operates 
            The ArguMentor application (the "Service"). This Privacy Policy describes how we collect, use, disclose, 
            and protect your information when you use the Service.
          </p>
          <p>
            By using the Service, you consent to the practices described in this Privacy Policy. If you do not agree, 
            please discontinue use of the Service.
          </p>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">1. INFORMATION WE COLLECT</h3>
            
            <p className="mb-2"><strong>1.1 Information You Provide Directly</strong></p>
            <p className="mb-1 pl-4">&#8226; <strong>Account Information:</strong> Name, email address</p>
            <p className="mb-1 pl-4">&#8226; <strong>Conflict Data:</strong> Problem statements (who, what, where, when, how), desired outcomes, Because statements, paraphrases, alternative solutions, ratings, and votes</p>
            <p className="mb-1 pl-4">&#8226; <strong>Messages:</strong> Mentor chat messages, inline comments</p>
            <p className="mb-1 pl-4">&#8226; <strong>Mood Data:</strong> Daily mood check-in ratings and timestamps</p>
            <p className="mb-2 pl-4">&#8226; <strong>Agreement Records:</strong> Electronic signature data, timestamps, and acknowledgment records</p>

            <p className="mb-2"><strong>1.2 Information Collected Automatically</strong></p>
            <p className="mb-1 pl-4">&#8226; <strong>Device Information:</strong> Browser type, user agent string, device identifiers</p>
            <p className="mb-1 pl-4">&#8226; <strong>Usage Data:</strong> Feature usage patterns, timestamps, session durations</p>
            <p className="mb-2 pl-4">&#8226; <strong>Push Notification Tokens:</strong> Firebase Cloud Messaging tokens for sending notifications to your devices</p>

            <p className="mb-2"><strong>1.3 Information from Third-Party Services</strong></p>
            <p className="pl-4">&#8226; <strong>Authentication Data:</strong> If you sign in via Google, we receive your name and email address from the identity provider. We do not receive or store your password.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">2. HOW WE USE YOUR INFORMATION</h3>
            <p className="mb-2">We use your information for the following purposes:</p>
            <p className="mb-1 pl-4">&#8226; To provide, maintain, and improve the Service</p>
            <p className="mb-1 pl-4">&#8226; To authenticate your identity and manage your account</p>
            <p className="mb-1 pl-4">&#8226; To facilitate conflict resolution between participants</p>
            <p className="mb-1 pl-4">&#8226; To send push notifications (mood reminders, conflict updates, safety alerts)</p>
            <p className="mb-1 pl-4">&#8226; To send email notifications (invitations, verification codes, safety alerts)</p>
            <p className="mb-1 pl-4">&#8226; To monitor for safety concerns (see Section 5 below)</p>
            <p className="mb-1 pl-4">&#8226; To generate aggregate, anonymized analytics</p>
            <p className="pl-4">&#8226; To enforce our Terms of Service</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">3. HOW WE SHARE YOUR INFORMATION</h3>
            <p className="mb-2"><strong>3.1 With Other Participants.</strong> Conflict data (statements, paraphrases, alternatives, votes) is shared between all participants and mentors assigned to a conflict. This is core to the Service's functionality.</p>
            <p className="mb-2"><strong>3.2 With Mentors.</strong> Mentors assigned to a conflict can view participant activity, conflict progress, and may receive safety alerts about participants.</p>
            <p className="mb-2"><strong>3.3 With Service Providers.</strong> We use the following third-party service providers:</p>
            <p className="mb-1 pl-4">&#8226; <strong>Google Firebase:</strong> Database storage, authentication, cloud functions, and push notifications (see <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">Firebase Privacy Policy</a>)</p>
            <p className="mb-1 pl-4">&#8226; <strong>EmailJS:</strong> Transactional email delivery for invitations and verification codes (see <a href="https://www.emailjs.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">EmailJS Privacy Policy</a>)</p>
            <p className="mb-2 pl-4">&#8226; <strong>Vercel:</strong> Application hosting (see <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">Vercel Privacy Policy</a>)</p>
            <p className="mb-2"><strong>3.4 For Legal Reasons.</strong> We may disclose your information if required by law, subpoena, court order, or government request, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</p>
            <p><strong>3.5 No Sale of Data.</strong> We do not sell, rent, or trade your personal information to third parties for marketing purposes.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">4. DATA STORAGE AND SECURITY</h3>
            <p className="mb-2"><strong>4.1 Storage.</strong> Your data is stored in Firebase Realtime Database, hosted by Google Cloud Platform, with servers located in the United States.</p>
            <p className="mb-2"><strong>4.2 Security Measures.</strong> We implement reasonable administrative, technical, and physical security measures, including encrypted data transmission (HTTPS/TLS), Firebase Security Rules for access control, and email-based authentication.</p>
            <p className="mb-2"><strong>4.3 No Guarantee.</strong> Despite our efforts, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your data.</p>
            <p><strong>4.4 Breach Notification.</strong> In the event of a data breach that materially affects your personal information, we will make reasonable efforts to notify affected users within 72 hours of discovery via email.</p>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-bold text-blue-900 mb-2">5. SAFETY MONITORING AND HARM DETECTION</h3>
            <p className="text-blue-800 mb-2">
              The Service includes an automated content monitoring system that scans user-submitted text for language 
              indicating potential self-harm, suicidal ideation, or threats of harm to others. This monitoring operates 
              as follows:
            </p>
            <p className="text-blue-800 mb-1 pl-4">&#8226; <strong>Detection:</strong> Text is analyzed locally within the application using pattern matching. No text is sent to external AI or analysis services for this purpose.</p>
            <p className="text-blue-800 mb-1 pl-4">&#8226; <strong>User Notification:</strong> If concerning language is detected, crisis resources (hotline numbers, text lines) are displayed to the user.</p>
            <p className="text-blue-800 mb-1 pl-4">&#8226; <strong>Mentor Notification:</strong> If a mentor is assigned to the conflict, they may be notified that concerning content was detected. The notification includes the user's name, conflict title, and the type of concern, but does NOT include the actual text submitted.</p>
            <p className="text-blue-800 mb-1 pl-4">&#8226; <strong>Safety Log:</strong> A minimal safety event record (user ID, timestamp, type of concern, context) is stored for auditing purposes. The actual text is NOT stored in the safety log.</p>
            <p className="text-blue-800 pl-4">&#8226; <strong>No Blocking:</strong> The safety system never prevents a user from submitting their content. It operates transparently alongside normal functionality.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">6. DATA RETENTION</h3>
            <p className="mb-2"><strong>6.1 Active Data.</strong> Your account information, conflict data, and mood data are retained for as long as your account is active or as needed to provide the Service.</p>
            <p className="mb-2"><strong>6.2 Deleted Accounts.</strong> When you request account deletion, your login credentials and personal profile (name, email, avatar) are permanently removed from our active systems within 30 days. Your private data (mood entries, push notification tokens, and free-tier tool history) is permanently deleted. Conflict content that involves other participants is retained in pseudonymized form (see § 6.5).</p>
            <p className="mb-2"><strong>6.3 Backup Retention.</strong> Residual copies may remain in backup systems for up to 90 days after deletion.</p>
            <p className="mb-2"><strong>6.4 Free-Tier Data Retention.</strong> For users on the free tier of the Communication Tools (Say It Your Way, Mood Journal, Vote Mode), data (including session history, mood check-ins, and closed votes) is automatically removed after 30 days. Paid plans retain data indefinitely while the subscription is active.</p>
            <p className="mb-2"><strong>6.5 Legal Hold and Pseudonymization.</strong> Conflict content that you created or participated in (text statements, paraphrases, votes, audit logs, and similar records) is retained in pseudonymized form under a sealed legal-hold record for <strong>seven (7) years</strong> from the date of your account deletion. During this period, your identity in those records is replaced with the label &quot;Deleted User&quot; and cannot be linked back to you in normal operation. Only an authorized representative may consult the sealed record in response to valid legal process, for example a subpoena, court order, or discovery request in a family-law, custody, civil, or similar proceeding in which your conflict records may be relevant. After the seven-year period elapses, the sealed record and the pseudonymized content (for conflicts in which you were the sole participant) are permanently destroyed. This retention is necessary to (i) preserve evidence that may be required in legal proceedings related to the conflicts you participated in, and (ii) protect the rights of other participants in shared conflicts whose communications would otherwise be lost. By using the Service, you acknowledge and consent to this retention.</p>
            <p><strong>6.6 Account Deletion Methods.</strong> You may request account deletion at any time by (i) using the &quot;Delete My Account&quot; option in your Profile Settings within the app, or (ii) submitting a request through the public deletion form at app.theargumentor.com/delete-account. Both methods invoke the same process described in §§ 6.2 and 6.5.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">7. YOUR RIGHTS</h3>
            <p className="mb-2">Depending on your jurisdiction, you may have the following rights regarding your personal information:</p>
            <p className="mb-1 pl-4">&#8226; <strong>Access:</strong> Request a copy of the personal information we hold about you</p>
            <p className="mb-1 pl-4">&#8226; <strong>Correction:</strong> Request correction of inaccurate or incomplete information</p>
            <p className="mb-1 pl-4">&#8226; <strong>Deletion:</strong> Request deletion of your personal information</p>
            <p className="mb-1 pl-4">&#8226; <strong>Portability:</strong> Request a machine-readable copy of your data</p>
            <p className="mb-2 pl-4">&#8226; <strong>Opt-Out:</strong> Disable push notifications through your device or browser settings</p>
            <p>To exercise any of these rights, contact us at <strong>info@4hci.com</strong>. We will respond to requests within 30 days.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">8. CHILDREN'S PRIVACY</h3>
            <p>
              The Service is not directed to children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If we become aware that a child under 13 has provided us with 
              personal information, we will take steps to delete such information. If you believe we have collected 
              information from a child under 13, please contact us at <strong>info@4hci.com</strong>.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">9. COOKIES AND LOCAL STORAGE</h3>
            <p>
              The Service uses browser localStorage to store session information (device identifiers, cached user data) 
              for authentication and performance purposes. The Service does not use tracking cookies or third-party 
              advertising cookies. Firebase may use cookies or similar technologies as part of its authentication and 
              analytics services.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">10. THIRD-PARTY LINKS</h3>
            <p>
              The Service may contain links to third-party websites or services (e.g., crisis hotlines, our company 
              website). We are not responsible for the privacy practices or content of these third-party sites. We 
              encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">11. CALIFORNIA PRIVACY RIGHTS (CCPA)</h3>
            <p className="mb-2">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <p className="mb-1 pl-4">&#8226; The right to know what personal information is collected, used, and shared</p>
            <p className="mb-1 pl-4">&#8226; The right to delete personal information</p>
            <p className="mb-1 pl-4">&#8226; The right to opt-out of the sale of personal information (we do not sell your data)</p>
            <p className="pl-4">&#8226; The right to non-discrimination for exercising your privacy rights</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">12. CHANGES TO THIS PRIVACY POLICY</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify users of material changes by 
              posting the updated policy within the Service and updating the "Last Updated" date. Your continued 
              use of the Service after changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">13. CONTACT US</h3>
            <p className="mb-2">If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
            <p className="pl-4"><strong>The ArguMentor, LLC</strong></p>
            <p className="pl-4">Land O' Lakes, Florida</p>
            <p className="pl-4">Email: <strong>info@4hci.com</strong></p>
            <p className="pl-4">Website: <a href="https://www.4hci.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">www.4hci.com</a></p>
          </div>
        </div>

        <div className="p-4 border-t border-stone-200">
          <button
            onClick={onClose}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

// === ACCESSIBILITY STATEMENT ===

function AccessibilityStatementModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <ModalDialog onClose={onClose} labelledBy="accessibility-statement-title" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 id="accessibility-statement-title" className="text-xl font-bold text-stone-800">Accessibility Statement</h2>
              <p className="text-xs text-stone-500 mt-1">The ArguMentor, LLC</p>
            </div>
            <button onClick={onClose} className="text-stone-500 hover:text-stone-600 transition-colors" aria-label="Close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5 text-sm text-stone-700 leading-relaxed" tabIndex={0} role="region" aria-label="Accessibility Statement content">
          <div>
            <h3 className="font-bold text-stone-900 mb-2">Our Commitment</h3>
            <p>
              The ArguMentor, LLC is committed to ensuring that our conflict resolution platform is accessible to all users,
              including people with disabilities. We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1
              at the AA level and continuously work to improve the user experience for everyone.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">Accessibility Features</h3>
            <p className="mb-2">The ArguMentor application includes the following accessibility features:</p>
            <p className="mb-1 pl-4">&#8226; <strong>Keyboard Navigation:</strong> All interactive elements (buttons, forms, modals, and navigation) are accessible via keyboard (Tab, Enter, Escape).</p>
            <p className="mb-1 pl-4">&#8226; <strong>Screen Reader Support:</strong> Semantic HTML, ARIA labels, and role attributes are used throughout the application to support assistive technologies.</p>
            <p className="mb-1 pl-4">&#8226; <strong>Color Contrast:</strong> Text and interactive elements meet WCAG AA contrast ratio requirements against their backgrounds.</p>
            <p className="mb-1 pl-4">&#8226; <strong>Focus Indicators:</strong> Visible focus rings are provided on all interactive elements for keyboard users.</p>
            <p className="mb-1 pl-4">&#8226; <strong>Responsive Design:</strong> The application is fully usable on mobile devices, tablets, and desktops of all screen sizes.</p>
            <p className="mb-1 pl-4">&#8226; <strong>Voice Input:</strong> Speech-to-text input is available for text fields, supporting users who have difficulty typing.</p>
            <p className="mb-1 pl-4">&#8226; <strong>Clear Language:</strong> Plain, concise language is used throughout the interface to support comprehension.</p>
            <p className="pl-4">&#8226; <strong>Modal Dialogs:</strong> All modals use proper ARIA roles (<code>role="dialog"</code>, <code>aria-modal="true"</code>) and an accessible name, trap focus within the dialog while open, close with Escape when a close option is available, and return focus to the control that opened them.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">Known Limitations</h3>
            <p className="mb-2">While we strive for full accessibility, some areas may have limitations:</p>
            <p className="mb-1 pl-4">&#8226; Some dynamically generated content (real-time notifications, chat messages) may not be immediately announced by all screen readers.</p>
            <p className="mb-1 pl-4">&#8226; Animal avatar illustrations are decorative and may not convey meaningful information to screen reader users.</p>
            <p className="pl-4">&#8226; Third-party components (Google sign-in button, Stripe payment forms) are governed by their respective providers' accessibility standards.</p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">Assistive Technology Compatibility</h3>
            <p>
              The ArguMentor is designed to work with modern assistive technologies, including NVDA, JAWS, VoiceOver (macOS/iOS),
              and TalkBack (Android). We test with the latest versions of Chrome, Safari, Firefox, and Edge.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">Feedback and Contact</h3>
            <p className="mb-2">
              We welcome your feedback on the accessibility of The ArguMentor. If you encounter any barriers or have
              suggestions for improvement, please contact us:
            </p>
            <p className="pl-4"><strong>Email:</strong> info@4hci.com</p>
            <p className="pl-4"><strong>Website:</strong> <a href="https://www.theargumentor.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 underline">www.theargumentor.com</a></p>
            <p className="mt-2">
              We aim to respond to accessibility feedback within 5 business days and to resolve reported issues as quickly as possible.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-stone-900 mb-2">Continuous Improvement</h3>
            <p>
              Accessibility is an ongoing effort. We regularly review and update the application to address accessibility
              concerns, incorporate user feedback, and align with evolving standards and best practices.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-stone-200">
          <button
            onClick={onClose}
            className="w-full bg-amber-700 hover:bg-amber-800 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalDialog>
  );
}

// === TEAM MOOD OVERVIEW (Admin / Mentor / Observer / Global) ===

export { DisclaimerModal, TermsOfServiceModal, PrivacyPolicyModal, AccessibilityStatementModal };
